import { isDefined } from '@/utils/general'
import { DeleteActionCellRenderer } from '@components/DriverTracking/Dwell/components/DeleteActionCell'
import { DwellOverrideCell } from '@components/DriverTracking/Dwell/components/DwellSiteGrid/DwellOverrideCell'
import { parseTimeStringToMinutes } from '@components/_SiteWide/Form/HourMinuteInput'
import { NumberCellEditor } from '@components/_SiteWide/Grid/CellEditors/NumberCellEditor'
import { durationFormatter } from '@utils/cell_formatter'
import { getStoreNumberComparator } from '@utils/grid'
import { getDateAtTimeString } from '@utils/intl/dateTimeFormatters'
import type { ColDef } from 'ag-grid-community'

const MAX_MINUTES = 60 * 12

export const getColumnDefs = (
  siteNames: string[],
  productGroups: string[],
  createHandleOverride: unknown,
  createHandleDeleteOverride: unknown,
  createHandleDelete: unknown,
  hasEdits: boolean
): (
  | { headerName: string; children: ColDef[]; marryChildren: boolean }
  | {
      suppressFiltersToolPanel: boolean
      headerName: string
      suppressColumnsToolPanel: boolean
      children: ColDef[]
      marryChildren: boolean
      suppressMovable: boolean
    }
)[] => {
  const siteInformationColumns: ColDef[] = [
    {
      headerName: 'Site Number',
      field: 'site_number',
      editable: (params) => params.data.$inserted,
      comparator: getStoreNumberComparator('site_number'),
      cellEditor: 'agRichSelectCellEditor',
      cellEditorParams: {
        values: siteNames,
      },
    },
    {
      headerName: 'Site Name',
      field: 'site_name',
    },
    {
      headerName: 'Product Group',
      field: 'product_group',
      editable: (params) => params.data.$inserted,
      valueFormatter: (params) => (params.value === 'default' ? '' : params.value),
      cellEditor: 'agRichSelectCellEditor',
      cellEditorParams: {
        values: productGroups,
      },
    },
    {
      headerName: 'Market',
      field: 'market',
    },
    {
      headerName: 'Sector',
      field: 'sector',
    },
  ]
  const dwellConfigurationColumns: ColDef[] = [
    {
      headerName: 'Set Dwell',
      field: 'set_dwell',
      headerTooltip: 'Time spent at the location. Will update projected ETAs for drivers stopping at this location.',
      editable: true,
      enableRowGroup: false,
      filter: 'agNumberColumnFilter',
      sortable: true,
      cellEditor: NumberCellEditor,
      valueFormatter: (params) => (isDefined(params.value) ? durationFormatter(params.value) : ''),
      valueParser: (params) => {
        return parseTimeStringToMinutes(params.newValue) || params.newValue
      },
      valueSetter: (params) => {
        if (params.newValue < 0) return false
        if (params.newValue > 721) return false
        if (isDefined(params.newValue)) {
          params.data.set_dwell = parseInt(params.newValue, 0)
          return true
        }
        return false
      },
    },
    {
      headerName: 'Payroll Override',
      field: 'payroll_override',
      headerTooltip: 'Time spent at the location for payroll purposes',
      editable: true,
      filter: 'agNumberColumnFilter',
      cellEditor: NumberCellEditor,
      valueFormatter: (params) => (isDefined(params.value) ? durationFormatter(params.value) : ''),
      valueParser: (params) => {
        return parseTimeStringToMinutes(params.newValue) || params.newValue
      },
      valueSetter: (params) => {
        if (params.newValue < 0) return false
        if (params.newValue > 721) return false
        if (isDefined(params.newValue)) {
          params.data.payroll_override = parseInt(params.newValue, 0)
        } else {
          params.data.payroll_override = null
        }
        return true
      },
    },
    {
      headerName: 'Temp Override',
      headerTooltip:
        'Temporary override of the set dwell time. Will update projected ETAs for drivers stopping at this location while active.',
      enableRowGroup: false,
      sortable: false,
      filter: false,
      cellRenderer: (params) => (
        <DwellOverrideCell
          params={params}
          createHandleOverride={createHandleOverride}
          createHandleDeleteOverride={createHandleDeleteOverride}
          hasEdits={hasEdits}
        />
      ),
    },
    {
      headerName: 'Override Expiration ',
      field: 'override_expiry',
      headerTooltip: 'Time when the temporary override will expire.',
      filter: 'agDateColumnFilter',
      enableRowGroup: false,
      valueGetter: (params) =>
        params.data?.override_expiry
          ? getDateAtTimeString(params.data.override_expiry, params.data.override_expiry_local)
          : '',
    },
    {
      headerName: 'Actions',
      enableRowGroup: false,
      sortable: false,
      filter: false,
      cellRenderer: (params) => (
        <DeleteActionCellRenderer params={params} createHandleDelete={createHandleDelete} hasEdits={hasEdits} />
      ),
    },
  ]

  return [
    { headerName: 'Site Information', marryChildren: true, children: siteInformationColumns },
    {
      headerName: 'Dwell Configuration',
      marryChildren: true,
      children: dwellConfigurationColumns,
      suppressMovable: true,
      suppressColumnsToolPanel: true,
      suppressFiltersToolPanel: true,
    },
  ]
}
